import axios, {
  type AxiosInstance,
  type AxiosResponse,
  type AxiosError,
} from "axios";
import { ElMessage } from "element-plus";
import { useAuthStore } from "@/stores/auth";

// Create axios instance
const http: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || "/api",
  timeout: 10000, // 10 seconds timeout - faster failure for better UX
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor
http.interceptors.request.use(
  (config) => {
    const authStore = useAuthStore();
    // Add token to all requests except public endpoints
    const isPublicEndpoint = config.url && (
      config.url.includes("/auth/") ||
      config.url.includes("/verification/check-email") ||
      config.url.includes("/verification/verify")
    );
    
    if (!isPublicEndpoint && authStore.token) {
      config.headers.Authorization = `Bearer ${authStore.token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor
http.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: AxiosError) => {
    const authStore = useAuthStore();

    // URLs that should handle errors silently
    const isRecommendationAPI = error.config?.url?.includes("/recommendations/");
    const isStatsAPI = error.config?.url?.includes("/stats/");
    const isUserUniversityAPI = error.config?.url?.includes("/users/me/university");
    const isVerificationDomainAPI = error.config?.url?.includes("/verification/domains");
    const shouldSuppressError = isRecommendationAPI || isStatsAPI || isUserUniversityAPI || isVerificationDomainAPI;

    // Enhanced error logging (suppress for silent APIs when 404)
    // Only suppress if: silent API && 404 status
    const shouldLog = !(shouldSuppressError && error.response?.status === 404);
    
    if (shouldLog) {
      console.error("HTTP Error:", {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        hasResponse: !!error.response,
        hasRequest: !!error.request
      });
    }

    if (error.response) {
      const { status, data } = error.response;
      const isLoginAPI = error.config?.url?.includes("/login");

      switch (status) {
        case 401:
          // Don't show error for silent APIs
          if (shouldSuppressError) {
            return Promise.reject(error); // Reject silently
          }
          // Don't auto-logout for login API 401 errors (wrong credentials)
          if (isLoginAPI) {
            break;
          }
          // For other 401 errors, clear auth state and redirect to login
          authStore.logout();
          ElMessage.error("Login expired, please login again");
          window.location.href = "/login";
          break;
        case 403:
          if (!shouldSuppressError) {
            ElMessage.error("No permission to access this resource");
          }
          break;
        case 404:
          // Don't show error for silent APIs (including user university API)
          if (!shouldSuppressError) {
            ElMessage.error("Requested resource not found");
          }
          break;
        case 409:
          // Conflict errors are handled at the component level (e.g., follow button)
          break;
        case 422:
          // Validation error
          if (data && typeof data === "object" && "message" in data) {
            ElMessage.error(data.message as string);
          } else {
            ElMessage.error("Request data validation failed");
          }
          break;
        case 500:
          if (!shouldSuppressError) {
            const errorMessage = data && typeof data === "object" && "message" in data 
              ? (data.message as string)
              : "Internal server error";
            ElMessage.error(errorMessage);
          }
          break;
        default:
          if (!shouldSuppressError) {
            const errorMessage = data && typeof data === "object" && "message" in data 
              ? (data.message as string)
              : "Request failed, please try again later";
            ElMessage.error(errorMessage);
          }
      }
    } else if (error.request) {
      if (!shouldSuppressError) {
        console.error("Network error details:", {
          message: error.message,
          code: error.code,
          config: {
            url: error.config?.url,
            method: error.config?.method,
            baseURL: error.config?.baseURL
          }
        });
        ElMessage.error(
          "Network connection failed, please check your network settings",
        );
      }
    } else {
      if (!shouldSuppressError) {
        ElMessage.error("Request configuration error");
      }
    }

    return Promise.reject(error);
  },
);

export { http };
